import React, { useState } from 'react'
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
} from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { Icon as CustomIcon, GroupAvatar, FeatureTooltip } from '@components'
import { MaterialIcons as Icon } from '@expo/vector-icons'
import { Scope } from '@utils'
import ScheduleCalendarModal from './components/ScheduleCalendarModal'
import dayjs from '../../../utils/dayjs'
import { SearchBar } from '@components'
import useCreateScheduledMessageCtrl from './useCreateScheduledMessageCtrl'
import { SafeAreaView } from 'react-native-safe-area-context'
import {
  Contact,
  useCreateScheduledMessage,
  useGenerateScheduledMessage,
  usePaginateContacts,
  usePaginateUserGroups,
  UserGroup,
  useScreen,
} from '@hooks'
import { defaultImage } from '@assets'
import { useLanguage } from '@context'
import { getCommonStyles } from '@utils'

type ScropeType = Scope.PRIVATE | Scope.PROFESSIONAL | Scope.GROUP | Scope.CATEGORY | Scope.HASHTAG

interface CreateScheduledMessageProps {
  scope?: ScropeType
}

const REASONS = [
  { key: 'birthday', label: 'Anniversaire', icon: 'cake' },
  { key: 'reconnect', label: 'Reprendre contact', icon: 'people' },
  { key: 'congratulations', label: 'Félicitations', icon: 'emoji-events' },
  { key: 'thank_you', label: 'Remerciement', icon: 'favorite' },
  { key: 'check_in', label: 'Prendre des nouvelles', icon: 'chat' },
  { key: 'new_year', label: 'Bonne année / Fêtes', icon: 'celebration' },
  { key: 'encouragement', label: 'Encouragement', icon: 'thumb-up' },
  { key: 'other', label: 'Autre', icon: 'edit' },
] as const

type Step = 'contact' | 'reason' | 'message'

const CreateScheduledMessage: React.FC<CreateScheduledMessageProps> = ({ scope: propScope }) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const commonStyles = getCommonStyles(theme)
  const { isStackVisible, goBack, navigateToCompose, getScopeFromRoute } =
    useCreateScheduledMessageCtrl()
  const { isIos, isWeb } = useScreen()
  // Use prop scope, route scope, or default to PRIVATE
  const scope = propScope || getScopeFromRoute() || Scope.PRIVATE

  const [step, setStep] = useState<Step>('contact')
  const [selectedContactId, setSelectedContactId] = useState<string>()
  const [selectedContactName, setSelectedContactName] = useState<string>('')
  const [selectedFilter, setSelectedFilter] = useState<ScropeType>(
    (scope as ScropeType) || Scope.PRIVATE,
  )
  const [searchText, setSearchText] = useState('')

  const [selectedReason, setSelectedReason] = useState<string>()
  const [additionalDetails, setAdditionalDetails] = useState('')

  const [isCalendarVisible, setIsCalendarVisible] = useState(false)
  const [date, setDate] = useState<string>(dayjs().format('DD-MM-YYYY'))
  const [time, setTime] = useState<string>(dayjs().add(1, 'hour').format('HH:mm'))
  const [message, setMessage] = useState<string>('')
  const [isSending, setIsSending] = useState(false)
  const [isWriteManually, setIsWriteManually] = useState(false)

  const isGroupTab = selectedFilter === Scope.GROUP

  const { flatData: contacts } = usePaginateContacts({
    search: searchText,
    scope: selectedFilter,
    noGhost: true,
  })
  const { flatData: groups } = usePaginateUserGroups({ search: searchText })
  const { mutateAsync: createScheduledMessage } = useCreateScheduledMessage()
  const { mutateAsync: generateMessage, isPending: isGenerating } = useGenerateScheduledMessage()

  const handleSelectContact = (contact: Contact) => {
    setSelectedContactId(contact.id)
    setSelectedContactName(contact.fullName)
  }

  const handleNextToReason = () => {
    if (selectedContactId) {
      setStep('reason')
    }
  }

  const handleGenerateMessage = async () => {
    if (!selectedContactId || !selectedReason) return

    try {
      const result = await generateMessage({
        recipientAccountId: selectedContactId,
        reason: selectedReason,
        additionalDetails: additionalDetails || undefined,
        scheduledDate: dayjs(date, 'DD-MM-YYYY').format('YYYY-MM-DD'),
      })
      setMessage(result.message)
      setStep('message')
    } catch {
      // Fallback to manual writing on error
      setStep('message')
    }
  }

  const handleWriteManually = () => {
    setIsWriteManually(true)
    setStep('message')
  }

  const handleSendMessage = async () => {
    if (selectedContactId && message) {
      setIsSending(true)
      const scheduledMessageId = await createScheduledMessage({
        ...(isGroupTab
          ? { groupId: selectedContactId }
          : { recipientAccountId: selectedContactId }),
        message,
        scheduledDate: dayjs(date, 'DD-MM-YYYY')
          .hour(parseInt(time.split(':')[0]))
          .minute(parseInt(time.split(':')[1]))
          .second(0)
          .utc()
          .toISOString(),
        reason: selectedReason,
      })
      setIsSending(false)
      navigateToCompose(scheduledMessageId)
    }
  }

  const handleBack = () => {
    if (step === 'message') {
      setStep('reason')
      setMessage('')
    } else if (step === 'reason') {
      setStep('contact')
      setSelectedReason(undefined)
      setAdditionalDetails('')
      setIsWriteManually(false)
    } else {
      goBack()
    }
  }

  const renderRecipientItem = (recipient: Contact | UserGroup) => {
    const isSelected = selectedContactId === recipient.id
    const name = 'fullName' in recipient ? recipient.fullName : (recipient.name ?? '')
    const avatarUrl = 'avatarUrl' in recipient ? recipient.avatarUrl : null
    return (
      <TouchableOpacity
        key={recipient.id}
        style={styles.contactItem}
        onPress={() => handleSelectContact(recipient)}
        activeOpacity={0.7}
      >
        {isGroupTab ? (
          <GroupAvatar profiles={(recipient as UserGroup).participants} />
        ) : (
          <Image
            source={avatarUrl ? { uri: avatarUrl } : defaultImage}
            style={styles.contactAvatar}
          />
        )}
        <View style={styles.contactInfo}>
          <Text style={styles.contactName}>{name}</Text>
        </View>
        <View style={[styles.checkbox, isSelected && styles.checkboxSelected]}>
          {isSelected && <Icon name="check" size={14} color="white" />}
        </View>
      </TouchableOpacity>
    )
  }

  const tabs = [
    { key: Scope.PRIVATE, label: t('scheduledMessagesScreen.private'), icon: 'lock' },
    { key: Scope.PROFESSIONAL, label: t('scheduledMessagesScreen.pro'), icon: 'work' },
    { key: Scope.GROUP, label: t('scheduledMessagesScreen.groups'), icon: 'people' },
  ]

  // STEP 1: Contact Selection
  if (step === 'contact') {
    return (
      <SafeAreaView style={styles.container}>
        <View style={[styles.header, isWeb && commonStyles.paddingTop]}>
          {!isStackVisible && (
            <TouchableOpacity onPress={handleBack}>
              <CustomIcon name="arrowBack" size={25} />
            </TouchableOpacity>
          )}
          <Text style={styles.headerTitle}>{t('scheduledMessagesScreen.chooseRecipient')}</Text>
        </View>

        <View style={styles.searchContainer}>
          <SearchBar value={searchText} onChangeText={setSearchText} />
        </View>

        <View style={styles.tabContainer}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.tabScrollContent}
          >
            {tabs.map((tab) => {
              const isActive = selectedFilter === tab.key
              const getActiveColor = () => {
                switch (tab.key) {
                  case Scope.PRIVATE:
                    return theme.colors.green
                  case Scope.PROFESSIONAL:
                    return theme.colors.primary
                  case Scope.GROUP:
                    return theme.colors.purple
                  default:
                    return theme.colors.primary
                }
              }
              const activeColor = getActiveColor()

              return (
                <TouchableOpacity
                  key={tab.key}
                  style={[
                    styles.tab,
                    isActive && { ...styles.activeTab, borderColor: activeColor },
                  ]}
                  onPress={() => setSelectedFilter(tab.key as ScropeType)}
                >
                  <CustomIcon
                    name={tab.icon as any}
                    size={16}
                    color={isActive ? activeColor : '#8E8E93'}
                  />
                  {tab.label ? (
                    <Text
                      style={[
                        styles.tabText,
                        isActive && { ...styles.activeTabText, color: activeColor },
                      ]}
                    >
                      {tab.label}
                    </Text>
                  ) : null}
                </TouchableOpacity>
              )
            })}
          </ScrollView>
        </View>

        <ScrollView style={styles.contactsList} showsVerticalScrollIndicator={false}>
          {(isGroupTab ? groups : contacts)?.map(renderRecipientItem)}
        </ScrollView>

        <View style={[styles.footer, !isIos && commonStyles.paddingBottom]}>
          <TouchableOpacity
            style={styles.calendarButton}
            onPress={() => setIsCalendarVisible(true)}
          >
            <CustomIcon name="calendar" size={20} color={theme.colors.green} />
          </TouchableOpacity>
          <View style={{ flex: 1, marginLeft: 12 }}>
            <TouchableOpacity
              style={[styles.nextButton, !selectedContactId && styles.nextButtonDisabled]}
              disabled={!selectedContactId}
              onPress={handleNextToReason}
            >
              <Text style={styles.nextButtonText}>{t('registerStep1.next')}</Text>
            </TouchableOpacity>
          </View>
        </View>

        <ScheduleCalendarModal
          isVisible={isCalendarVisible}
          setIsVisible={setIsCalendarVisible}
          date={date}
          time={time}
          onDateSelect={setDate}
          onTimeChange={setTime}
        />
      </SafeAreaView>
    )
  }

  // STEP 2: Reason Selection
  if (step === 'reason') {
    return (
      <SafeAreaView style={styles.container}>
        <View style={[styles.header, isWeb && commonStyles.paddingTop]}>
          <TouchableOpacity onPress={handleBack}>
            <CustomIcon name="arrowBack" size={25} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>
            {t('scheduledMessage.create.messageForContact', { name: selectedContactName })}
          </Text>
        </View>

        <FeatureTooltip
          id="scheduled_msg_reason"
          title={t('featureTooltip.scheduled_msg_reason.title')}
          description={t('featureTooltip.scheduled_msg_reason.description')}
          icon="star"
        />

        <ScrollView style={styles.reasonList} showsVerticalScrollIndicator={false}>
          <Text style={styles.sectionTitle}>{t('scheduledMessage.create.reasonQuestion')}</Text>

          {REASONS.map((reason) => {
            const isSelected = selectedReason === reason.key
            return (
              <TouchableOpacity
                key={reason.key}
                style={[styles.reasonItem, isSelected && styles.reasonItemSelected]}
                onPress={() => setSelectedReason(reason.key)}
                activeOpacity={0.7}
              >
                <Icon
                  name={reason.icon as any}
                  size={24}
                  color={isSelected ? theme.colors.primary : '#8E8E93'}
                />
                <Text style={[styles.reasonText, isSelected && styles.reasonTextSelected]}>
                  {reason.label}
                </Text>
                <View style={[styles.checkbox, isSelected && styles.checkboxSelected]}>
                  {isSelected && <Icon name="check" size={14} color="white" />}
                </View>
              </TouchableOpacity>
            )
          })}

          <Text style={styles.sectionTitle}>{t('scheduledMessage.create.additionalDetails')}</Text>
          <TextInput
            style={styles.detailsInput}
            placeholder={t('scheduledMessage.create.additionalDetailsPlaceholder')}
            placeholderTextColor="#8E8E93"
            value={additionalDetails}
            onChangeText={setAdditionalDetails}
            multiline
            numberOfLines={3}
          />
        </ScrollView>

        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          <View style={styles.reasonFooter}>
            <TouchableOpacity
              style={[
                styles.generateButton,
                (!selectedReason || isGenerating) && styles.generateButtonDisabled,
              ]}
              disabled={!selectedReason || isGenerating}
              onPress={handleGenerateMessage}
            >
              {isGenerating ? (
                <ActivityIndicator size="small" color="white" />
              ) : (
                <CustomIcon name="star" size={18} color="white" />
              )}
              <Text style={styles.generateButtonText}>
                {isGenerating ? 'Génération...' : "Générer avec l'IA"}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity style={styles.manualButton} onPress={handleWriteManually}>
              <CustomIcon name="edit" size={18} color={theme.colors.primary} />
              <Text style={styles.manualButtonText}>
                {t('scheduledMessage.create.writeManually')}
              </Text>
            </TouchableOpacity>
          </View>
        </KeyboardAvoidingView>
      </SafeAreaView>
    )
  }

  // STEP 3: Message Review & Send
  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={handleBack}>
          <CustomIcon name="arrowBack" size={20} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>
          {isWriteManually ? 'Écrire votre message' : 'Vérifier et envoyer'}
        </Text>
      </View>

      <ScrollView style={styles.messageReview} showsVerticalScrollIndicator={false}>
        <View style={styles.recipientBadge}>
          <Text style={styles.recipientBadgeLabel}>
            {t('scheduledMessage.create.recipientBadge')}
          </Text>
          <Text style={styles.recipientBadgeName}>{selectedContactName}</Text>
        </View>

        {selectedReason && !isWriteManually && (
          <View style={styles.reasonBadge}>
            <Text style={styles.reasonBadgeLabel}>{t('scheduledMessage.create.reasonBadge')}</Text>
            <Text style={styles.reasonBadgeName}>
              {REASONS.find((r) => r.key === selectedReason)?.label}
            </Text>
          </View>
        )}

        <View style={styles.scheduleBadge}>
          <CustomIcon name="calendar" size={16} color={theme.colors.green} />
          <Text style={styles.scheduleBadgeText}>
            {t('scheduledMessage.create.scheduledFor', {
              date: dayjs(date, 'DD-MM-YYYY').format('DD MMM YYYY'),
              time,
            })}
          </Text>
          <TouchableOpacity onPress={() => setIsCalendarVisible(true)}>
            <CustomIcon name="edit" size={14} color={theme.colors.primary} />
          </TouchableOpacity>
        </View>

        {!isWriteManually && message ? (
          <View style={styles.generatedMessageContainer}>
            <View style={styles.generatedMessageHeader}>
              <CustomIcon name="star" size={14} color={theme.colors.primary} />
              <Text style={styles.generatedMessageLabel}>{t('ai.generatedByAi')}</Text>
            </View>
            <TextInput
              style={styles.generatedMessageInput}
              value={message}
              onChangeText={setMessage}
              multiline
              numberOfLines={6}
            />
            <TouchableOpacity
              style={styles.regenerateButton}
              onPress={handleGenerateMessage}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <ActivityIndicator size="small" color={theme.colors.primary} />
              ) : (
                <CustomIcon name="star" size={14} color={theme.colors.primary} />
              )}
              <Text style={styles.regenerateButtonText}>
                {isGenerating ? 'Régénération...' : 'Régénérer'}
              </Text>
            </TouchableOpacity>
          </View>
        ) : null}
      </ScrollView>

      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <View style={[styles.footer, !isIos && commonStyles.paddingBottom]}>
          <TouchableOpacity
            style={styles.calendarButton}
            onPress={() => setIsCalendarVisible(true)}
          >
            <CustomIcon name="calendar" size={20} color={theme.colors.green} />
          </TouchableOpacity>
          <View style={styles.messageInputContainer}>
            <TextInput
              style={styles.messageInput}
              placeholder={t('scheduledMessagesScreen.typeMessage')}
              placeholderTextColor="#8E8E93"
              value={message}
              onChangeText={setMessage}
              multiline
            />
            <TouchableOpacity
              style={[
                styles.sendButton,
                (!message || !selectedContactId) && styles.sendButtonDisabled,
              ]}
              disabled={!message || !selectedContactId}
              onPress={handleSendMessage}
            >
              {isSending ? (
                <ActivityIndicator size="small" color={theme.colors.white} />
              ) : (
                <CustomIcon name="send" size={18} color="white" />
              )}
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>

      <ScheduleCalendarModal
        isVisible={isCalendarVisible}
        setIsVisible={setIsCalendarVisible}
        date={date}
        time={time}
        onDateSelect={setDate}
        onTimeChange={setTime}
      />
    </SafeAreaView>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingBottom: theme.spacing.md,
      backgroundColor: 'white',
      borderBottomWidth: 0.5,
      borderBottomColor: '#C7C7CC',
    },
    headerTitle: {
      fontSize: theme.fontSize.large,
      fontWeight: 'bold',
      color: theme.colors.text,
      flex: 1,
      textAlign: 'center',
    },
    searchContainer: {
      paddingHorizontal: 16,
      paddingVertical: 12,
      backgroundColor: 'white',
    },
    tabContainer: {
      backgroundColor: 'white',
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderBottomWidth: 0.5,
      borderBottomColor: '#E5E5EA',
    },
    tabScrollContent: {
      paddingHorizontal: 4,
    },
    tab: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingVertical: 8,
      marginRight: 12,
      borderRadius: 20,
      backgroundColor: '#F2F2F7',
      borderWidth: 1,
      borderColor: '#E5E5EA',
    },
    activeTab: {
      backgroundColor: 'white',
      borderColor: '#007AFF',
    },
    tabText: {
      fontSize: 14,
      color: '#8E8E93',
      fontWeight: '500',
      marginLeft: 6,
    },
    activeTabText: {
      color: '#007AFF',
      fontWeight: '600',
    },
    contactsList: {
      flex: 1,
      backgroundColor: 'white',
    },
    contactItem: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderBottomWidth: 0.5,
      borderBottomColor: '#E5E5EA',
    },
    contactAvatar: {
      width: 50,
      height: 50,
      borderRadius: 25,
      marginRight: 12,
    },
    contactInfo: {
      flex: 1,
    },
    contactName: {
      fontSize: 16,
      fontWeight: '400',
      color: '#000',
    },
    footer: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: 'white',
      paddingHorizontal: 16,
    },
    messageInputContainer: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: '#F2F2F7',
      borderRadius: 25,
      paddingRight: 5,
      paddingLeft: 16,
      paddingVertical: 5,
      marginLeft: 12,
    },
    messageInput: {
      flex: 1,
      fontSize: 16,
      color: '#000',
      paddingVertical: 0,
    },
    sendButton: {
      width: 36,
      height: 36,
      borderRadius: 18,
      backgroundColor: theme.colors.primary,
      justifyContent: 'center',
      alignItems: 'center',
      marginLeft: 8,
    },
    sendButtonDisabled: {
      opacity: 0.5,
    },
    calendarButton: {
      width: 44,
      height: 44,
      borderRadius: 22,
      backgroundColor: theme.colors.green + '1A',
      borderWidth: 1.5,
      borderColor: theme.colors.green,
      justifyContent: 'center',
      alignItems: 'center',
    },
    checkbox: {
      width: 20,
      height: 20,
      borderRadius: 10,
      borderWidth: 2,
      borderColor: theme.colors.lightGrey,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.white,
    },
    checkboxSelected: {
      backgroundColor: theme.colors.primary,
      borderColor: theme.colors.primary,
    },

    // Next button (step 1)
    nextButton: {
      backgroundColor: theme.colors.primary,
      borderRadius: 25,
      paddingVertical: 14,
      alignItems: 'center',
    },
    nextButtonDisabled: {
      opacity: 0.5,
    },
    nextButtonText: {
      fontSize: 16,
      fontWeight: '600',
      color: 'white',
    },

    // Reason selection (step 2)
    reasonList: {
      flex: 1,
      backgroundColor: 'white',
      paddingHorizontal: 16,
    },
    sectionTitle: {
      fontSize: 15,
      fontWeight: '600',
      color: '#000',
      marginTop: 20,
      marginBottom: 12,
    },
    reasonItem: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingVertical: 14,
      borderRadius: 12,
      backgroundColor: '#F2F2F7',
      marginBottom: 8,
    },
    reasonItemSelected: {
      backgroundColor: '#E8F0FE',
      borderWidth: 1,
      borderColor: theme.colors.primary,
    },
    reasonText: {
      flex: 1,
      fontSize: 16,
      color: '#000',
      marginLeft: 12,
    },
    reasonTextSelected: {
      color: theme.colors.primary,
      fontWeight: '500',
    },
    detailsInput: {
      backgroundColor: '#F2F2F7',
      borderRadius: 12,
      paddingHorizontal: 16,
      paddingVertical: 12,
      fontSize: 15,
      color: '#000',
      minHeight: 80,
      textAlignVertical: 'top',
    },
    reasonFooter: {
      backgroundColor: 'white',
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderTopWidth: 0.5,
      borderTopColor: '#C7C7CC',
      gap: 10,
    },
    generateButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.primary,
      borderRadius: 25,
      paddingVertical: 14,
      gap: 8,
    },
    generateButtonDisabled: {
      opacity: 0.5,
    },
    generateButtonText: {
      fontSize: 16,
      fontWeight: '600',
      color: 'white',
    },
    manualButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'white',
      borderRadius: 25,
      paddingVertical: 14,
      borderWidth: 1,
      borderColor: theme.colors.primary,
      gap: 8,
    },
    manualButtonText: {
      fontSize: 16,
      fontWeight: '500',
      color: theme.colors.primary,
    },

    // Message review (step 3)
    messageReview: {
      flex: 1,
      backgroundColor: 'white',
      paddingHorizontal: 16,
    },
    recipientBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: '#F2F2F7',
      borderRadius: 12,
      paddingHorizontal: 16,
      paddingVertical: 12,
      marginTop: 16,
    },
    recipientBadgeLabel: {
      fontSize: 14,
      color: '#8E8E93',
      marginRight: 8,
    },
    recipientBadgeName: {
      fontSize: 16,
      fontWeight: '500',
      color: '#000',
    },
    reasonBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: '#F2F2F7',
      borderRadius: 12,
      paddingHorizontal: 16,
      paddingVertical: 12,
      marginTop: 8,
    },
    reasonBadgeLabel: {
      fontSize: 14,
      color: '#8E8E93',
      marginRight: 8,
    },
    reasonBadgeName: {
      fontSize: 16,
      fontWeight: '500',
      color: '#000',
    },
    scheduleBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: '#F2F2F7',
      borderRadius: 12,
      paddingHorizontal: 16,
      paddingVertical: 12,
      marginTop: 8,
      gap: 8,
    },
    scheduleBadgeText: {
      flex: 1,
      fontSize: 15,
      color: '#000',
    },
    generatedMessageContainer: {
      marginTop: 16,
      backgroundColor: '#F8F9FF',
      borderRadius: 12,
      padding: 16,
      borderWidth: 1,
      borderColor: '#E0E4F2',
    },
    generatedMessageHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 12,
      gap: 6,
    },
    generatedMessageLabel: {
      fontSize: 13,
      color: theme.colors.primary,
      fontWeight: '500',
    },
    generatedMessageInput: {
      fontSize: 15,
      color: '#000',
      lineHeight: 22,
      minHeight: 100,
      textAlignVertical: 'top',
    },
    regenerateButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: 12,
      paddingVertical: 8,
      gap: 6,
    },
    regenerateButtonText: {
      fontSize: 14,
      color: theme.colors.primary,
      fontWeight: '500',
    },
  })

export default CreateScheduledMessage
